import { usePlan } from "../App";
import { CertInfo, ProbeResult } from "../types";
import { useNavigate } from "react-router-dom";

interface CertPanelProps {
  results: ProbeResult[];
}

function daysColor(days: number): string {
  if (days <= 0) return "#FF5C5C";
  if (days <= 14) return "#FF5C5C";
  if (days <= 30) return "#FDDD48";
  return "#39D98A";
}

function daysLabel(days: number): string {
  if (days <= 0) return "Expired";
  if (days === 1) return "1 day";
  return `${days} days`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function CertPanel({ results }: CertPanelProps) {
  const plan = usePlan();
  const navigate = useNavigate();

  // Only show if the URL was HTTPS — look for any result with cert_info.
  const certResult = results.find((r) => r.cert_info != null);
  if (!certResult && plan === "pro") return null;

  // For non-HTTPS endpoints, nothing to show.
  if (!certResult && plan !== "pro") return null;

  if (plan !== "pro") {
    // Show a locked teaser so free users know the feature exists.
    return (
      <div style={{
        marginTop: 16,
        padding: "14px 18px",
        borderRadius: 3,
        border: "1px solid rgba(255,255,255,0.1)",
        background: "rgba(18,18,18,0.6)",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 18 }}>🔒</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#EFF2F5" }}>SSL Certificate Inspector</div>
            <div style={{ fontSize: 12, color: "#98A1AB", marginTop: 2 }}>
              Expiry date, issuer, SANs, and TLS version — Pro feature
            </div>
          </div>
        </div>
        <button
          onClick={() => navigate("/upgrade")}
          className="btn-primary"
          style={{ fontSize: 12, padding: "6px 14px", whiteSpace: "nowrap" }}
        >
          Upgrade
        </button>
      </div>
    );
  }

  const cert = certResult!.cert_info as CertInfo;
  const days = cert.days_remaining;

  // Check if different regions have different expiry (misconfiguration signal).
  const certsWithInfo = results.filter((r) => r.cert_info != null);
  const uniqueExpiries = new Set(certsWithInfo.map((r) => r.cert_info!.not_after));
  const hasMismatch = uniqueExpiries.size > 1;

  return (
    <div style={{
      marginTop: 16,
      padding: "16px 18px",
      borderRadius: 3,
      border: `1px solid ${days <= 14 ? "rgba(255,92,92,0.3)" : "rgba(255,255,255,0.08)"}`,
      background: days <= 14 ? "rgba(255,92,92,0.05)" : "rgba(18,18,18,0.5)",
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: "#98A1AB", textTransform: "uppercase", letterSpacing: "0.06em" }}>
          SSL Certificate
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {hasMismatch && (
            <span style={{
              fontSize: 11, fontWeight: 600, color: "#FDDD48",
              background: "rgba(253,221,72,0.1)", padding: "2px 8px", borderRadius: 3,
            }}>
              Region mismatch
            </span>
          )}
          <span style={{
            fontSize: 12, fontWeight: 700,
            color: daysColor(days),
            background: `${daysColor(days)}18`,
            padding: "3px 10px",
            borderRadius: 3,
          }}>
            {days <= 0 ? "Expired" : `Expires in ${daysLabel(days)}`}
          </span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "10px 24px" }}>
        <CertField label="Subject" value={cert.subject || "—"} mono />
        <CertField label="Issuer" value={cert.issuer || "—"} />
        <CertField label="Valid from" value={formatDate(cert.not_before)} />
        <CertField label="Expires" value={formatDate(cert.not_after)} highlight={daysColor(days)} />
        <CertField label="TLS Version" value={cert.tls_version} />
        <CertField label="Cipher Suite" value={cert.cipher_suite} mono />
      </div>

      {cert.dns_names.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#98A1AB", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
            SANs ({cert.dns_names.length})
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {cert.dns_names.slice(0, 12).map((name) => (
              <span key={name} style={{
                fontSize: 11, fontFamily: "monospace",
                color: "#EFF2F5",
                background: "rgba(18,18,18,0.6)",
                border: "1px solid rgba(255,255,255,0.1)",
                padding: "2px 8px", borderRadius: 3,
              }}>
                {name}
              </span>
            ))}
            {cert.dns_names.length > 12 && (
              <span style={{ fontSize: 11, color: "#98A1AB", alignSelf: "center" }}>
                +{cert.dns_names.length - 12} more
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CertField({ label, value, mono, highlight }: {
  label: string;
  value: string;
  mono?: boolean;
  highlight?: string;
}) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#98A1AB", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 3 }}>
        {label}
      </div>
      <div style={{
        fontSize: 13,
        fontFamily: mono ? "monospace" : undefined,
        color: highlight ?? "#EFF2F5",
        fontWeight: highlight ? 600 : 400,
        wordBreak: "break-all",
      }}>
        {value}
      </div>
    </div>
  );
}
